import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Share2,
  Copy,
  Check,
  
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import {
  GiftIcon,
  UsersIcon,
  PromoIcon,
  EnvelopeIcon,
} from "@/components/icons/CustomIcons";


import { useAuth } from "@/hooks/use-auth";
import { getMyReferral, convertPointsToCoupon } from "@/lib/referrals.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/refer")({
  head: () => ({
    meta: [
      { title: "refer & earn — what" },
      { name: "description", content: "invite friends to what, earn points and turn them into discount coupons." },
      { property: "og:title", content: "Refer & Earn — What" },
      { property: "og:description", content: "Invite friends to what, earn points and turn them into discount coupons." },
      { property: "og:url", content: "https://whatfinland.com/refer" },
    ],
    links: [
      { rel: "canonical", href: "https://whatfinland.com/refer" },
    ],
  }),
  component: ReferPage,
});

function ReferPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [loading, user, navigate]);

  const fetchReferral = useServerFn(getMyReferral);
  const convertFn = useServerFn(convertPointsToCoupon);

  const { data, isLoading } = useQuery({
    queryKey: ["referral", user?.id],
    queryFn: () => fetchReferral(),
    enabled: !!user,
  });

  const convertMutation = useMutation({
    mutationFn: (points: number) => convertFn({ data: { points } }),
    onSuccess: (res) => {
      toast.success(`coupon ${res.coupon.code} created — €${res.coupon.value_eur} off`);
      queryClient.invalidateQueries({ queryKey: ["referral"] });
    },
    onError: (err: Error) => toast.error(err.message || "could not convert points"),
  });

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined" || !data?.code) return "";
    // Prefer the public custom domain for share links. If the user is
    // browsing the app on a Lovable preview/sandbox host, swap to the
    // canonical domain so shared links don't leak preview URLs.
    // When the app is hosted on any custom domain, that domain is used.
    const PREFERRED_DOMAIN = "https://whatfinland.com";
    const host = window.location.hostname;
    const isPreviewHost =
      host.endsWith(".lovableproject.com") ||
      host.endsWith(".lovable.app") ||
      host.endsWith(".lovable.dev") ||
      host === "localhost";
    const origin = isPreviewHost ? PREFERRED_DOMAIN : window.location.origin;
    return `${origin}/register?ref=${data.code}`;
  }, [data?.code]);


  const [copied, setCopied] = useState<"code" | "link" | null>(null);
  const [openPanel, setOpenPanel] = useState<"coupons" | "friends" | null>(null);
  const [friendsPage, setFriendsPage] = useState(1);
  const [couponsPage, setCouponsPage] = useState(1);
  const PAGE_SIZE = 10;
  function copy(value: string, which: "code" | "link") {
    if (!value) return;
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(which);
      toast.success("copied");
      setTimeout(() => setCopied(null), 1500);
    });
  }

  async function nativeShare() {
    if (!shareUrl) return;
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "what — finland's best spots",
          text: "join me on what and we both earn points toward discounts.",
          url: shareUrl,
        });
        return;
      } catch { /* user cancelled */ }
    }
    copy(shareUrl, "link");
  }

  if (loading || !user) {
    return <div className="min-h-[60vh] grid place-items-center text-sm text-slate-500">loading…</div>;
  }

  const balance = data?.balance ?? 0;
  const ppe = data?.pointsPerEuro ?? 20;
  const maxEuros = Math.floor(balance / ppe);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fbfd] via-white to-[#eef5fb]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0b1d3a] via-[#13325f] to-[#1f4a86] text-white p-6 sm:p-10 shadow-xl">
          <div className="absolute -top-12 -right-12 size-56 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-16 -left-10 size-64 rounded-full bg-amber-300/20 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] uppercase tracking-widest font-semibold backdrop-blur">
              <GiftIcon className="size-3" /> refer &amp; earn
            </div>
            <h1 className="mt-3 font-heading text-3xl sm:text-5xl leading-tight">
              invite friends.<br />earn discounts.
            </h1>
            <p className="mt-3 text-white/80 max-w-xl text-sm sm:text-base">
              every friend who joins what with your link gives you{" "}
              <span className="font-bold text-amber-300">100 points</span>.
              redeem points as coupons — {ppe} points = €1 off any booking.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 -mt-6 sm:-mt-8 relative z-10 px-2 sm:px-6">
          <StatTile
            label="points balance"
            value={isLoading ? "—" : balance.toLocaleString()}
            icon={GiftIcon}
            accent="from-amber-500 to-orange-500"
          />
          <StatTile
            label="friends joined"
            value={isLoading ? "—" : (data?.referrals.length ?? 0).toString()}
            icon={UsersIcon}
            accent="from-sky-500 to-indigo-500"
          />
          <StatTile
            label="active coupons"
            value={
              isLoading
                ? "—"
                : (data?.coupons.filter((c) => c.status === "active").length ?? 0).toString()
            }
            icon={GiftIcon}
            accent="from-emerald-500 to-teal-500"
          />
        </div>

        {/* Quick toggles: coupons & friends */}
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <PanelToggle
            open={openPanel === "coupons"}
            onClick={() => setOpenPanel((p) => (p === "coupons" ? null : "coupons"))}
            label="your coupons"
            count={data?.coupons.length ?? 0}
            tone="emerald"
            icon={PromoIcon}
          />
          <PanelToggle
            open={openPanel === "friends"}
            onClick={() => setOpenPanel((p) => (p === "friends" ? null : "friends"))}
            label="friends you referred"
            count={data?.referrals.length ?? 0}
            tone="indigo"
            icon={UsersIcon}
          />
        </div>

        {/* Collapsible panel content */}
        {openPanel === "coupons" && (
          <section className="mt-3 rounded-2xl bg-white border border-slate-200 p-5 sm:p-7 shadow-sm">
            {!data?.coupons.length ? (
              <EmptyState
                icon={PromoIcon}
                title="no coupons yet"
                body="convert points below to create your first coupon."
              />
            ) : (
              <>
                <ul className="grid gap-2.5 sm:grid-cols-2">
                  {data.coupons
                    .slice((couponsPage - 1) * PAGE_SIZE, couponsPage * PAGE_SIZE)
                    .map((c) => (
                    <li
                      key={c.id}
                      className="relative overflow-hidden rounded-xl border border-dashed border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
                          €{c.value_eur} off · {c.status}
                        </p>
                        <p className="font-mono text-lg font-bold text-slate-900 tracking-widest mt-0.5 truncate">
                          {c.code}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-1">
                          created {new Date(c.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => copy(c.code, "code")}
                        className="shrink-0 size-9 grid place-items-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 transition"
                        aria-label="copy coupon"
                      >
                        <Copy className="size-4" />
                      </button>
                    </li>
                  ))}
                </ul>
                <Pagination
                  page={couponsPage}
                  totalPages={Math.ceil(data.coupons.length / PAGE_SIZE)}
                  onPageChange={setCouponsPage}
                />
              </>
            )}
          </section>
        )}
        {openPanel === "friends" && (
          <section className="mt-3 rounded-2xl bg-white border border-slate-200 p-5 sm:p-7 shadow-sm">
            {!data?.referrals.length ? (
              <EmptyState
                icon={UsersIcon}
                title="no one yet"
                body="share your link to invite your first friend."
              />
            ) : (
              <>
                <ul className="divide-y divide-slate-100">
                  {data.referrals
                    .slice((friendsPage - 1) * PAGE_SIZE, friendsPage * PAGE_SIZE)
                    .map((r) => (
                    <li key={r.id} className="py-2.5 flex items-center gap-3">
                      {r.avatar ? (
                        <img src={r.avatar} alt="" className="size-9 rounded-full object-cover" />
                      ) : (
                        <div className="size-9 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 grid place-items-center text-white text-xs font-bold">
                          {r.name?.[0]?.toUpperCase() ?? "?"}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate leading-tight">{r.name}</p>
                        <p className="text-[11px] text-slate-500">{new Date(r.createdAt).toLocaleDateString()}</p>
                      </div>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                        <GiftIcon className="size-3" /> +{r.points} pts
                      </span>
                    </li>
                  ))}
                </ul>
                <Pagination
                  page={friendsPage}
                  totalPages={Math.ceil(data.referrals.length / PAGE_SIZE)}
                  onPageChange={setFriendsPage}
                />
              </>
            )}
          </section>
        )}

        {/* Share */}
        <section className="mt-8 sm:mt-10 rounded-2xl bg-white border border-slate-200 p-5 sm:p-7 shadow-sm">
          <h2 className="font-heading text-xl sm:text-2xl text-slate-900">share your link</h2>
          <p className="text-sm text-slate-500 mt-1">your unique code travels with the link.</p>

          <div className="mt-5 grid sm:grid-cols-[auto_1fr] gap-3 items-stretch">
            <div className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">code</span>
              <span className="font-mono text-lg font-bold text-slate-900 tracking-widest">
                {data?.code ?? "…"}
              </span>
              <button
                type="button"
                onClick={() => data?.code && copy(data.code, "code")}
                className="ml-1 size-8 grid place-items-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 transition"
                aria-label="copy code"
              >
                {copied === "code" ? <Check className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
              </button>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
              <Input
                value={shareUrl}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 border-0 focus-visible:ring-0 px-0 text-sm text-slate-700"
              />
              <Button type="button" size="sm" variant="outline" onClick={() => copy(shareUrl, "link")} className="gap-1.5">
                {copied === "link" ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
                {copied === "link" ? "copied" : "copy"}
              </Button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" onClick={nativeShare} className="bg-[#0b1d3a] hover:bg-[#0b1d3a]/90 gap-2">
              <Share2 className="size-4" /> share link
            </Button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`join me on what — finland's best spots: ${shareUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
            >
              <FaWhatsapp className="size-4 text-[#25D366]" /> whatsapp
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent("join me on what")}&body=${encodeURIComponent(`finland's best spots, curated. ${shareUrl}`)}`}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
            >
              <EnvelopeIcon className="size-4 text-sky-600" /> email
            </a>
          </div>
        </section>

        {/* Convert */}
        <section className="mt-6 rounded-2xl bg-gradient-to-br from-amber-50 via-white to-rose-50 border border-amber-200/60 p-5 sm:p-7">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-heading text-xl sm:text-2xl text-slate-900">convert points to coupon</h2>
              <p className="text-sm text-slate-600 mt-1">
                {ppe} points = €1 off. minimum {ppe * 5} points (€5 coupon).
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">available</p>
              <p className="font-heading text-2xl text-slate-900">{balance.toLocaleString()} pts</p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[5, 10, 20, 50].map((eur) => {
              const pts = eur * ppe;
              const canAfford = balance >= pts;
              return (
                <button
                  key={eur}
                  type="button"
                  disabled={!canAfford || convertMutation.isPending}
                  onClick={() => convertMutation.mutate(pts)}
                  className={`rounded-xl border p-3 text-left transition ${
                    canAfford
                      ? "border-amber-300 bg-white hover:border-amber-500 hover:shadow-md"
                      : "border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed"
                  }`}
                >
                  <p className="font-heading text-2xl text-slate-900">€{eur}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{pts.toLocaleString()} pts</p>
                  <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
                    convert <ArrowRight className="size-3" />
                  </p>
                </button>
              );
            })}
          </div>
          {maxEuros < 5 && (
            <p className="mt-4 text-xs text-slate-500">
              you need at least {ppe * 5} points to redeem your first €5 coupon.
            </p>
          )}
        </section>

        <div className="mt-10 text-center pb-6">
          <Link to="/profile" className="text-xs text-slate-500 hover:text-slate-900 underline">
            ← back to profile
          </Link>
        </div>
        {/* (coupons + friends are rendered above as collapsible panels) */}
      </div>
    </div>
  );
}

function StatTile({
  label, value, icon: Icon, accent,
}: { label: string; value: string; icon: React.ComponentType<{ className?: string }>; accent: string }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-4 shadow-sm">
      <div className={`size-8 rounded-lg bg-gradient-to-br ${accent} text-white grid place-items-center mb-2`}>
        <Icon className="size-4" />
      </div>
      <p className="font-heading text-xl sm:text-2xl text-slate-900 leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function PanelToggle({
  open, onClick, label, count, tone, icon: Icon,
}: {
  open: boolean;
  onClick: () => void;
  label: string;
  count: number;
  tone: "emerald" | "indigo";
  icon: React.ComponentType<{ className?: string }>;
}) {
  const toneCls =
    tone === "emerald"
      ? "from-emerald-500 to-teal-500"
      : "from-sky-500 to-indigo-500";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className={`group relative w-full text-left rounded-2xl border bg-white px-4 py-3.5 sm:px-5 sm:py-4 flex items-center gap-3 transition-all ${
        open
          ? "border-slate-900/20 shadow-lg ring-2 ring-slate-900/5"
          : "border-slate-200 hover:border-slate-300 hover:shadow-md"
      }`}
    >
      <span className={`shrink-0 size-10 rounded-xl bg-gradient-to-br ${toneCls} text-white grid place-items-center shadow-sm`}>
        <Icon className="size-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</p>
        <p className="font-heading text-lg leading-none mt-1 text-slate-900 tabular-nums">
          {count} {open ? "shown" : count === 1 ? "item" : "items"}
        </p>
      </div>
      <ChevronDown
        className={`shrink-0 size-4 text-slate-400 group-hover:text-slate-700 transition-transform ${
          open ? "rotate-180 text-slate-900" : ""
        }`}
      />
    </button>
  );
}

function Pagination({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  return (
    <div className="mt-4 flex items-center justify-center gap-1.5">
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPageChange(p)}
          className={`size-8 rounded-md text-sm font-semibold transition ${
            p === page
              ? "bg-[#0b1d3a] text-white"
              : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
          }`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}



function EmptyState({
  icon: Icon, title, body,
}: { icon: React.ComponentType<{ className?: string }>; title: string; body: string }) {
  return (
    <div className="text-center py-6">
      <div className="size-12 rounded-2xl bg-slate-100 grid place-items-center mx-auto mb-3">
        <Icon className="size-5 text-slate-400" />
      </div>
      <p className="font-semibold text-slate-900">{title}</p>
      <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">{body}</p>
    </div>
  );
}
